import { useEffect, useRef, type RefObject } from "react";

/**
 * Drives a hero's scroll transformation by writing `--hero-progress` (0 → 1)
 * onto the element. All the actual movement lives in CSS.
 *
 * ── Why a custom property and not React state ────────────────────────────────
 * The obvious version is `const [p, setP] = useState(0)` in a scroll handler.
 * That re-renders the whole subtree on every frame of every scroll, on the
 * page with the most DOM in the app. Writing one custom property mutates a
 * single style and lets the compositor do the rest, so this hook never causes a
 * render at all.
 *
 * ── Why not CSS scroll-driven animations ─────────────────────────────────────
 * `animation-timeline: scroll()` expresses this natively with no JS. It is
 * still not something to ship as the only implementation to an Australian
 * consumer audience, which is heavily iOS/Safari — a browser without it gets no
 * animation AND no static fallback unless you write the fallback anyway, at
 * which point you maintain two. This is one implementation that runs
 * everywhere.
 *
 * ── prefers-reduced-motion ───────────────────────────────────────────────────
 * Honoured by not running: progress is pinned at 0 and the hero simply sits in
 * its resting state. Watched live rather than read once, because the setting
 * can change mid-session and someone turning it on is asking for the motion to
 * stop NOW.
 *
 * NOT for the signed-in product. Ned, 2026-08-06: landing and information pages
 * only. A hero that moves while you are reading a number is the wrong instinct
 * inside a tool, and the chat page owns its own scroll container anyway — this
 * reads `window.scrollY`, which does not move there.
 */
export function useScrollHero<T extends HTMLElement>(
  /** Scroll distance, in px, over which progress goes 0 → 1. */
  distance = 420,
): RefObject<T | null> {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;

    const apply = () => {
      frame = 0;
      const progress = motion.matches ? 0 : Math.min(1, Math.max(0, window.scrollY / distance));
      /* Three decimals: enough that the movement reads as continuous, few
         enough that we are not writing a new string on every sub-pixel. */
      el.style.setProperty("--hero-progress", progress.toFixed(3));
    };

    /* rAF-coalesced. Scroll fires far more often than the screen refreshes, and
       without this the style write happens several times per painted frame. */
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(apply);
    };

    apply(); // deep link or restored scroll position — start in the right state
    window.addEventListener("scroll", onScroll, { passive: true });
    motion.addEventListener("change", apply);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      motion.removeEventListener("change", apply);
    };
  }, [distance]);

  return ref;
}
